import React, { useState, useEffect } from 'react';
import pwaManager from '../utils/pwaManager';

/**
 * PWA Install Button Component
 * Shows install prompt to users who can install the app
 */
const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [installInProgress, setInstallInProgress] = useState(false);

  useEffect(() => {
    pwaManager.init();

    // Listen for install prompt availability
    const handleInstallPrompt = () => {
      setShowPrompt(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setShowPrompt(false);
    };

    pwaManager.on('onInstallPrompt', handleInstallPrompt);
    pwaManager.on('onAppInstalled', handleAppInstalled);

    // Check initial state
    const status = pwaManager.getInstallationStatus();
    if (!status.isInstalled && status.canPromptInstall) {
      setShowPrompt(true);
    }

    return () => {
      pwaManager.off('onInstallPrompt', handleInstallPrompt);
      pwaManager.off('onAppInstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    setInstallInProgress(true);
    const success = await pwaManager.promptInstall();
    if (!success) {
      setInstallInProgress(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-indigo-600 text-white rounded-lg shadow-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1">Install App</h3>
            <p className="text-sm text-indigo-100">
              Get faster access and a better experience with our app installed on your device.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-2 text-indigo-200 hover:text-white focus:outline-none"
            aria-label="Dismiss"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleInstallClick}
            disabled={installInProgress}
            className="flex-1 bg-white text-indigo-600 font-medium py-2 px-4 rounded hover:bg-indigo-50 disabled:opacity-50"
          >
            {installInProgress ? 'Installing...' : 'Install'}
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 bg-indigo-700 text-white font-medium py-2 px-4 rounded hover:bg-indigo-800"
          >
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
